/**
 * router — Portal v2 routes (DEC-063). Generated; re-emitted by
 * `codaic add service` / patched by `codaic init-wiki`.
 */

import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import AppLayout from './layouts/AppLayout';
import LoginPage from './pages/LoginPage';
import GlobalHome from './pages/GlobalHome';
import ModuleHome from './pages/ModuleHome';
import SettingsPage from './pages/Settings';
import { useAuth } from './auth/AuthProvider';

const suspenseFallbackSx = { display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 };

const S: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Suspense
    fallback={
      <Box sx={suspenseFallbackSx}>
        <CircularProgress size={24} />
      </Box>
    }
  >
    {children}
  </Suspense>
);

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

// ===== Event Monitor (built-in) =====
const EventMonitorPage = lazy(() => import('./modules/events/pages/EventMonitorPage'));

// ===== Forge Admin Dashboard =====
const ForgeAdminPage = lazy(() => import('./modules/forge/ForgeAdminPage'));

// ===== Lazy-imported codegen pages from service frontends =====
const TransformationDashboard = lazy(() => import('@tran/dashboard/DashboardPage'));
const TransformationGenericEntityPage = lazy(() => import('@tran/generic/GenericEntityPage').then((mod) => ({ default: mod.GenericEntityPage || mod.default })));




// Transformation Plan (portal-local pages, declared in portal.custom-pages.yaml)
const TpCeoView = lazy(() => import('./modules/transformation-plan/pages/CeoView'));
const TpPmoControl = lazy(() => import('./modules/transformation-plan/pages/PmoControl'));
const TpFunctionWorkspace = lazy(() => import('./modules/transformation-plan/pages/FunctionWorkspace'));
const TpMyWeekWizard = lazy(() => import('./modules/transformation-plan/pages/MyWeekWizard'));
const TpBoardPack = lazy(() => import('./modules/transformation-plan/pages/BoardPack'));
const TpMeetingMode = lazy(() => import('./modules/transformation-plan/pages/MeetingMode'));
const TpActivityFeed = lazy(() => import('./modules/transformation-plan/pages/ActivityFeed'));

// Wiki
const WikiIndexPage = lazy(() => import('./modules/wiki/pages/WikiIndexPage'));
const WikiPageView = lazy(() => import('./modules/wiki/pages/WikiPageView'));
const WikiIngestPanel = lazy(() => import('./modules/wiki/pages/WikiIngestPanel'));
const WikiLintReport = lazy(() => import('./modules/wiki/pages/WikiLintReport'));

export function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />

      <Route
        path="/"
        element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<GlobalHome />} />
        <Route path="dashboard" element={<S><GlobalHome /></S>} />
        <Route path="settings" element={<SettingsPage />} />

        {/* Events module */}
        <Route path="events" element={<S><EventMonitorPage /></S>} />

        {/* Forge admin */}
        <Route path="forge/admin" element={<S><ForgeAdminPage /></S>} />

        {/* Module home pages */}
        <Route path="transformation" element={<S><ModuleHome moduleId="transformation" /></S>} />

        {/* Module dashboards */}
        <Route path="transformation/dashboard" element={<S><TransformationDashboard /></S>} />

        {/* Admin CRUD pages (from service frontends) */}
        <Route path="transformation/admin/:entitySlug" element={<S><TransformationGenericEntityPage /></S>} />
      
      {/* Transformation Plan */}
      <Route path="ceo" element={<S><TpCeoView /></S>} />
      <Route path="pmo" element={<S><TpPmoControl /></S>} />
      <Route path="f/:code" element={<S><TpFunctionWorkspace /></S>} />
      <Route path="f/:code/update" element={<S><TpMyWeekWizard /></S>} />
      <Route path="board-pack" element={<S><TpBoardPack /></S>} />
      <Route path="meeting" element={<S><TpMeetingMode /></S>} />
      <Route path="activity" element={<S><TpActivityFeed /></S>} />

      {/* Wiki */}
      <Route path="wiki" element={<S><WikiIndexPage /></S>} />
      <Route path="wiki/page/:slug" element={<S><WikiPageView /></S>} />
      <Route path="wiki/ingest" element={<S><WikiIngestPanel /></S>} />
      <Route path="wiki/lint" element={<S><WikiLintReport /></S>} />
</Route>
    </Routes>
  );
}
